import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Form, Grid, Header, Message } from 'semantic-ui-react';
import {
  API,
  downloadTextAsFile,
  showError,
  showSuccess,
  timestamp2string,
  verifyJSON,
} from '../helpers';
import PricingEditor from './PricingEditor';

const OperationSetting = () => {
  const { t } = useTranslation();
  let now = new Date();
  let [inputs, setInputs] = useState({
    QuotaForNewUser: 0,
    QuotaForInviter: 0,
    QuotaForInvitee: 0,
    QuotaRemindThreshold: 0,
    PreConsumedQuota: 0,
    TopUpLink: '',
    ChatLink: '',
    QuotaPerUnit: 0,
    AutomaticDisableChannelEnabled: '',
    AutomaticEnableChannelEnabled: '',
    ChannelDisableThreshold: 0,
    LogConsumeEnabled: '',
    RequestLogEnabled: '',
    RequestLogRetentionDays: 60,
    RequestLogMaxSize: 104857600,
    RequestLogPayloadMode: 'summary',
    RequestLogSuccessBudgetBytes: 4096,
    RequestLogFailureBudgetBytes: 16384,
    DisplayInCurrencyEnabled: '',
    DisplayTokenStatEnabled: '',
    ApproximateTokenEnabled: '',
    RetryTimes: 0,
  });
  const [pricingConfig, setPricingConfig] = useState('');
  const [pricingStats, setPricingStats] = useState(null);
  const [originInputs, setOriginInputs] = useState({});
  const [pricingFileInputKey, setPricingFileInputKey] = useState(0);
  const pricingFileInputRef = useRef(null);
  let [loading, setLoading] = useState(false);
  let [historyTimestamp, setHistoryTimestamp] = useState(
    timestamp2string(now.getTime() / 1000 - 30 * 24 * 3600)
  ); // a month ago

  const getOptions = async () => {
    const res = await API.get('/api/option/');
    const { success, message, data } = res.data;
    if (success) {
      let newInputs = {};
      data.forEach((item) => {
        if (item.value === '{}') {
          item.value = '';
        }
        newInputs[item.key] = item.value;
      });
      setInputs(newInputs);
      setOriginInputs(newInputs);
    } else {
      showError(message);
    }
  };

  const getPricingConfig = async () => {
    const res = await API.get('/api/pricing/config?raw=1');
    const { success, message, data } = res.data;
    if (success) {
      // 后端返回的data可能是字符串或对象，需要判断
      if (typeof data === 'string') {
        // 如果是字符串，直接使用
        setPricingConfig(data);
      } else {
        // 如果是对象，转换为格式化的JSON字符串
        setPricingConfig(JSON.stringify(data, null, 2));
      }
    } else {
      showError(message);
    }
  };

  const getPricingStats = async () => {
    const res = await API.get('/api/pricing/stats');
    const { success, message, data } = res.data;
    if (success) {
      setPricingStats(data);
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    getOptions().then();
    getPricingConfig().then();
    getPricingStats().then();
  }, []);

  const updateOption = async (key, value) => {
    setLoading(true);
    if (key.endsWith('Enabled')) {
      value = inputs[key] === 'true' ? 'false' : 'true';
    }
    const res = await API.put('/api/option/', {
      key,
      value,
    });
    const { success, message } = res.data;
    if (success) {
      setInputs((inputs) => ({ ...inputs, [key]: value }));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const handleInputChange = async (e, { name, value }) => {
    if (name.endsWith('Enabled')) {
      await updateOption(name, value);
    } else {
      setInputs((inputs) => ({ ...inputs, [name]: value }));
    }
  };

  const submitConfig = async (group) => {
    switch (group) {
      case 'request-log':
        for (const key of [
          'RequestLogPayloadMode',
          'RequestLogSuccessBudgetBytes',
          'RequestLogFailureBudgetBytes',
          'RequestLogRetentionDays',
          'RequestLogMaxSize',
        ]) {
          if (originInputs[key] !== inputs[key])
            await updateOption(key, String(inputs[key]));
        }
        break;
      case 'monitor':
        if (
          originInputs['ChannelDisableThreshold'] !==
          inputs.ChannelDisableThreshold
        ) {
          await updateOption(
            'ChannelDisableThreshold',
            inputs.ChannelDisableThreshold
          );
        }
        if (
          originInputs['QuotaRemindThreshold'] !== inputs.QuotaRemindThreshold
        ) {
          await updateOption(
            'QuotaRemindThreshold',
            inputs.QuotaRemindThreshold
          );
        }
        break;
      case 'quota':
        if (originInputs['QuotaForNewUser'] !== inputs.QuotaForNewUser) {
          await updateOption('QuotaForNewUser', inputs.QuotaForNewUser);
        }
        if (originInputs['QuotaForInvitee'] !== inputs.QuotaForInvitee) {
          await updateOption('QuotaForInvitee', inputs.QuotaForInvitee);
        }
        if (originInputs['QuotaForInviter'] !== inputs.QuotaForInviter) {
          await updateOption('QuotaForInviter', inputs.QuotaForInviter);
        }
        if (originInputs['PreConsumedQuota'] !== inputs.PreConsumedQuota) {
          await updateOption('PreConsumedQuota', inputs.PreConsumedQuota);
        }
        break;
      case 'general':
        if (originInputs['TopUpLink'] !== inputs.TopUpLink) {
          await updateOption('TopUpLink', inputs.TopUpLink);
        }
        if (originInputs['ChatLink'] !== inputs.ChatLink) {
          await updateOption('ChatLink', inputs.ChatLink);
        }
        if (originInputs['QuotaPerUnit'] !== inputs.QuotaPerUnit) {
          await updateOption('QuotaPerUnit', inputs.QuotaPerUnit);
        }
        if (originInputs['RetryTimes'] !== inputs.RetryTimes) {
          await updateOption('RetryTimes', inputs.RetryTimes);
        }
        break;
      default:
        break;
    }
  };

  const deleteHistoryLogs = async () => {
    console.log(inputs);
    const res = await API.delete(
      `/api/log/?target_timestamp=${Date.parse(historyTimestamp) / 1000}`
    );
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(`${data} 条日志已清理！`);
      return;
    }
    showError('日志清理失败：' + message);
  };

  const updatePricingConfig = async () => {
    if (!verifyJSON(pricingConfig)) {
      showError('定价配置不是合法的 JSON 字符串');
      return;
    }
    setLoading(true);
    const res = await API.put('/api/pricing/config', {
      config: pricingConfig,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('定价配置更新成功');
      await getPricingStats();
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const reloadPricingConfig = async () => {
    setLoading(true);
    const res = await API.post('/api/pricing/reload');
    const { success, message } = res.data;
    if (success) {
      showSuccess('定价配置已重新加载');
      await getPricingConfig();
      await getPricingStats();
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const savePricingConfig = async () => {
    setLoading(true);
    const res = await API.post('/api/pricing/save', {
      config: pricingConfig,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('定价配置已保存到集中配置');
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const exportPricingConfig = async () => {
    try {
      const res = await API.get('/api/pricing/export');
      downloadTextAsFile(
        typeof res.data === 'string'
          ? res.data
          : JSON.stringify(res.data, null, 2),
        'pricing_config.json'
      );
      showSuccess('定价配置已下载');
    } catch (error) {
      showError(error.message || '导出定价配置失败');
    }
  };

  const exportPricingConfigToFile = async () => {
    setLoading(true);
    try {
      const res = await API.post('/api/pricing/export-to-file');
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || '定价配置已导出到本地文件');
      } else {
        showError(message || '导出定价配置失败');
      }
    } catch (error) {
      showError(error.message || '导出定价配置失败');
    }
    setLoading(false);
  };

  const importPricingConfig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    try {
      const res = await API.post('/api/pricing/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || '定价配置导入成功');
        await getPricingConfig();
        await getPricingStats();
      } else {
        showError(message || '导入定价配置失败');
      }
    } catch (error) {
      showError(error.message || '导入定价配置失败');
    }
    setPricingFileInputKey((key) => key + 1);
    setLoading(false);
  };

  const triggerPricingImport = () => {
    pricingFileInputRef.current?.click();
  };

  const restorePricingConfig = async () => {
    setLoading(true);
    const res = await API.post('/api/pricing/restore');
    const { success, message } = res.data;
    if (success) {
      showSuccess('定价配置已恢复为默认配置');
      await getPricingConfig();
      await getPricingStats();
    } else {
      showError(message);
    }
    setLoading(false);
  };

  return (
    <Grid columns={1}>
      <Grid.Column>
        <Form loading={loading}>
          <Header as='h3'>{t('setting.operation.quota.title')}</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('setting.operation.quota.new_user')}
              name='QuotaForNewUser'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForNewUser}
              type='number'
              min='0'
              placeholder={t('setting.operation.quota.new_user_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.quota.pre_consume')}
              name='PreConsumedQuota'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.PreConsumedQuota}
              type='number'
              min='0'
              placeholder={t('setting.operation.quota.pre_consume_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.quota.inviter_reward')}
              name='QuotaForInviter'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForInviter}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.quota.inviter_reward_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.quota.invitee_reward')}
              name='QuotaForInvitee'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForInvitee}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.quota.invitee_reward_placeholder'
              )}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('quota').then();
            }}
          >
            {t('setting.operation.quota.buttons.save')}
          </Form.Button>
          <Divider />
          <Header as='h3'>{t('setting.operation.pricing.title')}</Header>
          <Message>
            {t('setting.operation.pricing.description')}
            <br />
            <strong>支持的定价类型：</strong>
            <ul style={{ marginTop: '10px' }}>
              <li>simple: 简单定价（固定输入/输出价格）</li>
              <li>conditional: 条件定价（根据token范围分段计费）</li>
              <li>matrix: 矩阵定价（所有组合具体价格）</li>
              <li>multiplier: 倍率定价（基础价格×倍率）</li>
              <li>per_char: 按字符计费（音频）</li>
              <li>per_request: 按请求计费（音频）</li>
              <li>per_second: 按时长计费（音频）</li>
            </ul>
            <strong>币种说明：</strong>
            usd=美元，cny=人民币，双币种独立定价，不通过汇率转换
          </Message>

          <PricingEditor
            config={pricingConfig}
            onConfigChange={(newConfig) => setPricingConfig(newConfig)}
            stats={pricingStats}
          />

          <Form.Group style={{ marginTop: '20px' }}>
            <Form.Button onClick={updatePricingConfig}>
              {t('setting.operation.pricing.buttons.update')}
            </Form.Button>
            <Form.Button onClick={reloadPricingConfig}>
              {t('setting.operation.pricing.buttons.reload')}
            </Form.Button>
            <Form.Button onClick={savePricingConfig}>
              保存到集中配置
            </Form.Button>
            <Form.Button onClick={exportPricingConfig}>下载 JSON</Form.Button>
            <Form.Button onClick={exportPricingConfigToFile}>
              导出到本地文件
            </Form.Button>
            <Form.Button type='button' onClick={triggerPricingImport}>
              导入 JSON 文件
            </Form.Button>
            <input
              key={pricingFileInputKey}
              ref={pricingFileInputRef}
              type='file'
              accept='.json,application/json'
              onChange={importPricingConfig}
              style={{ display: 'none' }}
            />
            <Form.Button color='orange' onClick={restorePricingConfig}>
              {t('setting.operation.pricing.buttons.restore')}
            </Form.Button>
          </Form.Group>

          <Divider />
          <Header as='h3'>{t('setting.operation.log.title')}</Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.LogConsumeEnabled === 'true'}
              label={t('setting.operation.log.enable_consume')}
              name='LogConsumeEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.RequestLogEnabled === 'true'}
              label={t('setting.operation.log.enable_request_log')}
              name='RequestLogEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths={4}>
            <Form.Input
              label={t('setting.operation.log.request_log_retention_days')}
              name='RequestLogRetentionDays'
              type='number'
              value={inputs.RequestLogRetentionDays}
              onChange={handleInputChange}
              placeholder='60'
            />
            <Form.Input
              label={t('setting.operation.log.request_log_max_size')}
              name='RequestLogMaxSize'
              type='number'
              value={inputs.RequestLogMaxSize}
              onChange={handleInputChange}
              placeholder='104857600'
            />
          </Form.Group>
          <Message info>
            请求日志只保存摘要，主库与日志库使用同一规则，不保存完整对话、回答或
            Base64。
            正文预览和原文归档暂未开放。保留天数用于既有删除记录任务，不是正文精简周期。
          </Message>
          <Form.Group widths='equal'>
            <Form.Select
              label='请求日志模式'
              name='RequestLogPayloadMode'
              value={inputs.RequestLogPayloadMode || 'summary'}
              options={[
                {
                  key: 'summary',
                  value: 'summary',
                  text: '摘要（参数与追踪信息）',
                },
                { key: 'metadata', value: 'metadata', text: '最小元信息' },
              ]}
              onChange={handleInputChange}
            />
            <Form.Input
              label='成功请求总报文预算（字节）'
              name='RequestLogSuccessBudgetBytes'
              type='number'
              min='2048'
              max='4096'
              value={inputs.RequestLogSuccessBudgetBytes || 4096}
              onChange={handleInputChange}
            />
            <Form.Input
              label='失败请求总报文预算（字节）'
              name='RequestLogFailureBudgetBytes'
              type='number'
              min='2048'
              max='16384'
              value={inputs.RequestLogFailureBudgetBytes || 16384}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            type='button'
            onClick={() => submitConfig('request-log')}
          >
            保存日志配置
          </Form.Button>
          <Form.Group widths={4}>
            <Form.Input
              label={t('setting.operation.log.target_time')}
              value={historyTimestamp}
              type='datetime-local'
              name='history_timestamp'
              onChange={(e, { name, value }) => {
                setHistoryTimestamp(value);
              }}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              deleteHistoryLogs().then();
            }}
          >
            {t('setting.operation.log.buttons.clean')}
          </Form.Button>

          <Divider />
          <Header as='h3'>{t('setting.operation.monitor.title')}</Header>
          <Form.Group widths={3}>
            <Form.Input
              label={t('setting.operation.monitor.max_response_time')}
              name='ChannelDisableThreshold'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.ChannelDisableThreshold}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.monitor.max_response_time_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.monitor.quota_reminder')}
              name='QuotaRemindThreshold'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaRemindThreshold}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.monitor.quota_reminder_placeholder'
              )}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutomaticDisableChannelEnabled === 'true'}
              label={t('setting.operation.monitor.auto_disable')}
              name='AutomaticDisableChannelEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.AutomaticEnableChannelEnabled === 'true'}
              label={t('setting.operation.monitor.auto_enable')}
              name='AutomaticEnableChannelEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('monitor').then();
            }}
          >
            {t('setting.operation.monitor.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3'>{t('setting.operation.general.title')}</Header>
          <Form.Group widths={4}>
            <Form.Input
              label={t('setting.operation.general.topup_link')}
              name='TopUpLink'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.TopUpLink}
              type='link'
              placeholder={t(
                'setting.operation.general.topup_link_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.general.chat_link')}
              name='ChatLink'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.ChatLink}
              type='link'
              placeholder={t('setting.operation.general.chat_link_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.general.quota_per_unit')}
              name='QuotaPerUnit'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaPerUnit}
              type='number'
              step='0.01'
              placeholder={t(
                'setting.operation.general.quota_per_unit_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.general.retry_times')}
              name='RetryTimes'
              type={'number'}
              step='1'
              min='0'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.RetryTimes}
              placeholder={t(
                'setting.operation.general.retry_times_placeholder'
              )}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.DisplayInCurrencyEnabled === 'true'}
              label={t('setting.operation.general.display_in_currency')}
              name='DisplayInCurrencyEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.DisplayTokenStatEnabled === 'true'}
              label={t('setting.operation.general.display_token_stat')}
              name='DisplayTokenStatEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.ApproximateTokenEnabled === 'true'}
              label={t('setting.operation.general.approximate_token')}
              name='ApproximateTokenEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('general').then();
            }}
          >
            {t('setting.operation.general.buttons.save')}
          </Form.Button>
        </Form>
      </Grid.Column>
    </Grid>
  );
};

export default OperationSetting;
